/**
 * Authentication Service
 * Firebase Auth operations and admin management
 */

import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  type User,
  type Unsubscribe,
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, limit, getDocs } from 'firebase/firestore'
import { auth, db, COLLECTIONS } from '../config/firebase'

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  isAdmin: boolean
}

export interface AdminRecord {
  uid: string
  email: string
  displayName: string
  addedAt: Date
  addedBy: string
}

// ============================================================================
// Auth State
// ============================================================================

const googleProvider = new GoogleAuthProvider()

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(
  callback: (user: AuthUser | null) => void
): Unsubscribe {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      callback(null)
      return
    }

    const isAdmin = await checkIsAdmin(firebaseUser.uid, firebaseUser.email)

    callback({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      isAdmin,
    })
  })
}

/**
 * Get current user (one-time check)
 */
export function getCurrentUser(): User | null {
  return auth.currentUser
}

/**
 * Get current auth user with admin status
 */
export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  const user = auth.currentUser

  if (!user) {
    return null
  }

  const isAdmin = await checkIsAdmin(user.uid, user.email)

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    isAdmin,
  }
}

// ============================================================================
// Sign In / Sign Out
// ============================================================================

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<AuthUser> {
  const result = await signInWithPopup(auth, googleProvider)
  const user = result.user

  // Record user in Firestore
  await recordUser(user)

  const isAdmin = await checkIsAdmin(user.uid, user.email)

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    isAdmin,
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth)
}

// ============================================================================
// Admin Management
// ============================================================================

/**
 * Check if user is an admin
 * Uses email-based lookup with isAdmin flag (legacy schema)
 */
export async function checkIsAdmin(uid: string, email?: string | null): Promise<boolean> {
  try {
    // First try UID-based lookup (new schema)
    const adminDocByUid = await getDoc(doc(db, COLLECTIONS.ADMINS, uid))
    if (adminDocByUid.exists()) {
      return true
    }

    // Fallback to email-based lookup (legacy schema)
    if (email) {
      const adminsRef = collection(db, COLLECTIONS.ADMINS)
      const q = query(
        adminsRef,
        where('email', '==', email),
        where('isAdmin', '==', true),
        limit(1)
      )
      const snapshot = await getDocs(q)
      return !snapshot.empty
    }

    return false
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

/**
 * Add a user as admin (requires current user to be admin)
 */
export async function addAdmin(
  uid: string,
  email: string,
  displayName: string
): Promise<void> {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Must be signed in to add admin')
  }

  const isCurrentUserAdmin = await checkIsAdmin(currentUser.uid)
  if (!isCurrentUserAdmin) {
    throw new Error('Only admins can add new admins')
  }

  await setDoc(doc(db, COLLECTIONS.ADMINS, uid), {
    uid,
    email,
    displayName,
    addedAt: serverTimestamp(),
    addedBy: currentUser.uid,
  })
}

/**
 * Remove admin status (requires current user to be admin)
 */
export async function removeAdmin(uid: string): Promise<void> {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Must be signed in to remove admin')
  }

  const isCurrentUserAdmin = await checkIsAdmin(currentUser.uid)
  if (!isCurrentUserAdmin) {
    throw new Error('Only admins can remove admins')
  }

  // Don't allow removing self
  if (uid === currentUser.uid) {
    throw new Error('Cannot remove your own admin status')
  }

  const { deleteDoc } = await import('firebase/firestore')
  await deleteDoc(doc(db, COLLECTIONS.ADMINS, uid))
}

// ============================================================================
// User Management
// ============================================================================

/**
 * Record user in Firestore for tracking
 */
async function recordUser(user: User): Promise<void> {
  const userRef = doc(db, COLLECTIONS.USERS, user.uid)
  const userDoc = await getDoc(userRef)

  if (!userDoc.exists()) {
    // New user
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    })
  } else {
    // Existing user - update last login
    await setDoc(
      userRef,
      {
        lastLogin: serverTimestamp(),
        displayName: user.displayName,
        photoURL: user.photoURL,
      },
      { merge: true }
    )
  }
}

/**
 * Get user by ID
 */
export async function getUser(uid: string): Promise<AuthUser | null> {
  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid))

  if (!userDoc.exists()) {
    return null
  }

  const data = userDoc.data()
  const isAdmin = await checkIsAdmin(uid)

  return {
    uid,
    email: data.email as string | null,
    displayName: data.displayName as string | null,
    photoURL: data.photoURL as string | null,
    isAdmin,
  }
}
